import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_METADATA = {
  siteUrl: "https://www.truevined.com",
  siteName: "Truevined",
  title: "Truevined - Vancouver Hip-Hop Artist | Official Website",
  description: "Official website of Truevined, a Vancouver-based hip-hop artist and producer. Explore music, albums, videos, and Christian worship tracks.",
  author: "@Truevined",
  image: "https://www.truevined.com/og-image.jpg",
};

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string;
  author?: string;
  lang?: string;
  meta?: MetaTag[];
  structuredData?: Record<string, unknown>;
  noindex?: boolean;
}

function SEO({
  title = "",
  description = "",
  image = SITE_METADATA.image,
  url,
  type = "website",
  keywords = "",
  author = SITE_METADATA.author,
  lang = "en",
  meta = [],
  structuredData,
  noindex = false,
}: SEOProps) {
  const metaTitle = title ? `${title} | ${SITE_METADATA.siteName}` : SITE_METADATA.title;
  const metaDescription = description || SITE_METADATA.description;
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const canonicalUrl = url
    ? (url.startsWith("http") ? url : `${SITE_METADATA.siteUrl}${url}`)
    : `${SITE_METADATA.siteUrl}${currentPath}`;
  const fullUrl = canonicalUrl;

  const metaTags = [
    {
      name: "description",
      content: metaDescription,
    },
    {
      name: "keywords",
      content: keywords || "Truevined, hip-hop, rapper, producer, Vancouver, music",
    },
    {
      name: "author",
      content: author,
    },
    {
      property: "og:title",
      content: metaTitle,
    },
    {
      property: "og:description",
      content: metaDescription,
    },
    {
      property: "og:type",
      content: type,
    },
    {
      property: "og:url",
      content: fullUrl,
    },
    {
      property: "og:site_name",
      content: SITE_METADATA.siteName,
    },
    ...(image
      ? [
          {
            property: "og:image",
            content: image,
          },
          {
            property: "og:image:width",
            content: "1200",
          },
          {
            property: "og:image:height",
            content: "630",
          },
        ]
      : []),
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:creator",
      content: author,
    },
    {
      name: "twitter:title",
      content: metaTitle,
    },
    {
      name: "twitter:description",
      content: metaDescription,
    },
    ...(image
      ? [
          {
            name: "twitter:image",
            content: image,
          },
        ]
      : []),
  ];

  const robots = noindex ? [{ name: "robots", content: "noindex, follow" }] : [];
  const finalMeta = (meta ? metaTags.concat(meta as typeof metaTags) : metaTags).concat(robots);

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={metaTitle}
      meta={finalMeta as any}
      link={[
        { rel: "alternate", type: "application/rss+xml", title: "Truevined music and arcade", href: "https://www.truevined.com/rss.xml" },
        { rel: "alternate", type: "application/atom+xml", title: "Truevined music and arcade", href: "https://www.truevined.com/atom.xml" },
        {
          rel: "canonical",
          href: fullUrl,
        },
      ]}
    >
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
